import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { searchAllCards, getSetCards, normalizeSetName, getSimilarCards } from '../services/pokemonTcgApi';
import { supabase } from '../lib/supabase';
import CardDetails from '../components/CardDetails';
import SimilarCards from '../components/SimilarCards';
import { ArrowLeft } from 'lucide-react';
import { transformCard } from '../utils/cardTransformer';
import { slugify } from '../utils/slugify';
import CardDetailsSkeleton from '../components/skeletons/CardDetailsSkeleton';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingHeader from '../components/LoadingHeader';

export default function CardPage() {
  const { series, setName, cardName } = useParams();
  const [card, setCard] = useState<Card | null>(null);
  const [similarCards, setSimilarCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const previousCardRef = useRef<{
    setName: string | null;
    cardName: string | null;
  }>({ setName: null, cardName: null });

  // Check if we came from the search page
  const isFromSearch = location.state?.fromSearch;
  const searchQuery = localStorage.getItem('pokemonTcgSearchHistory');

  useEffect(() => {
    const fetchCardData = async () => {
      if (!setName || !cardName) return;

      // Check if we're loading a different card
      if (
        previousCardRef.current.setName === setName && 
        previousCardRef.current.cardName === cardName
      ) {
        return;
      }

      // Update the previous card reference
      previousCardRef.current = { setName, cardName };
      
      // Reset state for new card
      setCard(null);
      setSimilarCards([]);
      setLoading(true);
      setError(null);
      setLoadingError(null);

      try {
        // Extract card number from the URL
        const parts = cardName.split('-');
        const cardNumber = parts.pop()?.trim() || '';
        const cardNameWithoutNumber = parts.join(' ');
        const decodedSetName = decodeURIComponent(setName).replace(/-/g, ' ');
        const decodedSeries = series ? decodeURIComponent(series).replace(/-/g, ' ') : '';

        console.log('Fetching card data:', {
          cardName: cardNameWithoutNumber,
          cardNumber,
          setName: decodedSetName,
          series: decodedSeries
        });

        // Handle trainer gallery cards
        const isTrainerGallery = decodedSetName.toLowerCase().includes('trainer gallery');
        let setId = normalizeSetName(decodedSetName);
        
        if (isTrainerGallery) {
          // Extract base set name and append TG suffix
          const baseSetName = decodedSetName.replace(/trainer gallery/i, '').trim();
          setId = `${normalizeSetName(baseSetName)}tg`;
        }

        if (!setId) {
          throw new Error(`Could not normalize set name: ${decodedSetName}`);
        }

        // Construct the card ID using the set ID and card number
        const cardId = `${setId}-${cardNumber}`;
        console.log('Looking up card by ID:', cardId);

        // Direct database query by ID
        const { data: initialCardData, error: cardError } = await supabase
          .from('pokemontcg_io')
          .select(`
            *,
            set:pokemontcg_io_sets(*)
          `)
          .eq('id', cardId)
          .maybeSingle();

        if (cardError) {
          console.error('Error fetching card:', cardError);
          throw cardError;
        }

        let finalCardData = initialCardData;

        if (!finalCardData) {
          console.log('Card not found by ID, falling back to search');
          
          // Try searching by name and number within the set
          const { data: searchData, error: searchError } = await supabase
            .from('pokemontcg_io')
            .select(`
              *,
              set:pokemontcg_io_sets(*)
            `)
            .eq('set_id', setId)
            .eq('number', cardNumber)
            .ilike('name', `%${cardNameWithoutNumber}%`)
            .maybeSingle();

          if (searchError || !searchData) {
            throw new Error('Card not found');
          }

          finalCardData = searchData;
        }

        // Transform the card data
        const transformedCard = {
          ...finalCardData,
          flavor: finalCardData.flavor_text, // Use flavor_text from database
          images: {
            small: finalCardData.image_small_url || `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${finalCardData.id}_small.webp`,
            large: finalCardData.image_large_url || `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${finalCardData.id}_large.webp`
          },
          set: {
            ...finalCardData.set,
            releaseDate: finalCardData.set.release_date,
            images: {
              symbol: finalCardData.set.symbol_url,
              logo: finalCardData.set.logo_url
            }
          }
        };

        setCard(transformedCard);
        
        // Fetch similar cards
        try {
          const similarCardsResults = await getSimilarCards(transformedCard);
          setSimilarCards(similarCardsResults);
        } catch (similarError) {
          console.error('Error fetching similar cards:', similarError);
        }
      } catch (error) {
        console.error('Error fetching card:', error);
        setError('Card not found');
      } finally {
        setLoading(false);
      }
    };

    fetchCardData();
  }, [setName, cardName, series, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <header className="bg-white/5 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <LoadingHeader />
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 py-8">
          <article className="bg-white/5 border border-white/10 rounded-lg">
            <CardDetailsSkeleton />
          </article>
        </main>
      </div>
    );
  }

  if (loadingError || error || !card) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <h1 className="text-2xl font-bold mb-4">Card Not Found</h1>
        <p className="text-white/60 mb-4">
          {loadingError || error || "We couldn't find the card you're looking for. This might be because:"}
        </p>
        {!loadingError && !error && (
          <ul className="list-disc text-white/60 mb-6 pl-6">
            <li>The URL might be incorrect</li>
            <li>The card number might be wrong</li>
            <li>The set name might not match exactly</li>
          </ul>
        )}
        <Link to="/" className="text-white/60 hover:text-white flex items-center gap-2">
          <ArrowLeft size={20} />
          Back to Home
        </Link>
      </div>
    );
  }

  const handleBack = () => {
    if (isFromSearch && searchQuery) {
      navigate('/', { state: { restoreSearch: true } });
    } else {
      navigate(`/${slugify(card.set.series)}/${slugify(card.set.name)}`);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              {isFromSearch && searchQuery ? 'Back to Search' : `Back to ${card.set.name}`}
            </button>
            <h1 className="text-xl font-bold">{card.name}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <article className="bg-white/5 border border-white/10 rounded-lg">
          <CardDetails card={card} />
        </article>

        {similarCards.length > 0 && (
          <SimilarCards cards={similarCards} currentCardId={card.id} />
        )}
      </main>
    </div>
  );
}